import { getRouteApi, useRouterState } from '@tanstack/react-router'
import { HistoryTable } from 'bank-entity-history/src/components/HistoryTable'
import { Form } from 'brief-form'
import { routes } from 'commons/constants/routes'
import { TableDataQa } from 'commons/types/enums'
import { formatCurrencyCent } from 'commons/utils'
import {
  CircularProgress,
  Info,
  InfoSet,
  Layer,
  Link,
  Page,
  PageFace,
  PageSection,
  PageSectionFace,
  Tooltip
} from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button, OutlineButton } from 'ui/components/Buttons'
import { formatDateTime } from 'ui/components/DateTime'
import { TextareaInput } from 'ui/components/InputV2'
import { Footer } from 'ui/temp/Footer'
import { LicensesTitle } from 'ui/temp/LicensesTitle'
import { Col, Col2x, FormRow, Row } from 'ui/themes/globalStyles'

import { MetrcSourceTable, POSSourceTable, RejectDialog, Sidebar, UploadedFilesTable } from './components'
import { useRejectionReason } from './useRejectionReason'
import { useSourceDetails } from './useSourceDetails'

import { s } from './styles'

const routeApi = getRouteApi('/_app/salesdata/$sourceId')

const SourceDetails = () => {
  const { sourceId } = routeApi.useParams()
  const routerState = useRouterState()
  const navigate = routeApi.useNavigate()

  const {
    webPageData,
    invalidate,
    isFetching,
    config,
    Field,
    saveInternalNotes,
    isInternalNotesSubmitting,
    ApproveConfirmation,
    approve,
    isShowRejectDialog,
    showRejectDialog,
    hideRejectDialog,
    historyPagination
  } = useSourceDetails(+sourceId)

  const {
    config: rejectionNotesConfig,
    Field: RejectionNotesField,
    isRejectionNotesSubmitting,
    saveRejectionNotes
  } = useRejectionReason(webPageData, invalidate)

  if (!webPageData) {
    return <CircularProgress centered />
  }

  const { reportType, infoReportName, infoReportId } = webPageData
  const returnUrl = routerState.location.pathname

  return (
    <Page
      face={PageFace.SECONDARY}
      title={webPageData.titleOverline}
      subTitle={webPageData.title}
      isPending={false}
      footer={
        <Footer>
          {webPageData.enableReject && (
            <OutlineButton face="negative" size="middle" onClick={() => showRejectDialog()}>
              Reject
            </OutlineButton>
          )}
          {webPageData.enableApprove && (
            <Button face="primary" onClick={() => approve()}>
              Approve
            </Button>
          )}
        </Footer>
      }
    >
      <PageSection
        title="Sales Data"
        anchor="main"
        face={PageSectionFace.SECONDARY}
        actions={
          <>
            {webPageData.enableEdit === false && webPageData.editButtonTooltip !== undefined && (
              <Tooltip id="edit" type="dark" content={webPageData.editButtonTooltip}>
                <Button face="secondary" disabled>
                  Edit
                </Button>
              </Tooltip>
            )}
            {webPageData.enableEdit && (
              <Button
                face="secondary"
                onClick={() =>
                  navigate({
                    to: '/salesdata/bank-source-override/$mrId/$licenseId',
                    params: {
                      mrId: webPageData.infoReportId.toString(),
                      licenseId: webPageData.infoLicenses[0].licenseId.toString()
                    },
                    search: { returnUrl, type: webPageData.reportType }
                  })
                }
              >
                Edit
              </Button>
            )}
          </>
        }
      >
        <Sidebar webPage={webPageData} />
        <ApproveConfirmation />
        <s.StyledHr />
        <br />
        <InfoSet>
          <Row>
            <Col>
              <Info label="Total Cannabis Sales Excluding Sales Tax">
                {formatCurrencyCent(webPageData.totalCannabisSales)}
              </Info>
            </Col>
            <Col>
              <Info label="Total Non-Cannabis Sales">{formatCurrencyCent(webPageData.totalNonCannabisSales)}</Info>
            </Col>
          </Row>
          <Row>
            <Col>
              <Info label={webPageData.totalSalesTaxCollectedLabel}>
                {formatCurrencyCent(webPageData.totalSalesTaxCollected)}
              </Info>
            </Col>
            <Col>
              <Info label="Total Sales">{formatCurrencyCent(webPageData.totalSales)}</Info>
            </Col>
          </Row>
          {webPageData.infoClientNotes !== undefined && (
            <Row>
              <Col2x>
                <br />
                <Info label="Customer Notes">{webPageData.infoClientNotes}</Info>
              </Col2x>
            </Row>
          )}
        </InfoSet>
        <InfoSet legend="General Information">
          <Row>
            <Col>
              <Info label="Report">
                {reportType === 'custom' ? (
                  <Link to={routes.bank.hrr.details} params={{ id: infoReportId.toString() }}>
                    {infoReportName}
                  </Link>
                ) : (
                  <Link to="/mr/$id" params={{ id: infoReportId.toString() }}>
                    {infoReportName}
                  </Link>
                )}
              </Info>
            </Col>
            <Col>
              <Info label={`License${webPageData.infoLicenses.length > 1 ? 's' : ''}`}>
                <LicensesTitle licenses={webPageData.infoLicenses} withUrl />
              </Info>
            </Col>
          </Row>

          <Row>
            <Col>
              <Info label="Status">{webPageData.infoStatus}</Info>
            </Col>
            <Col>
              <Info label="Last Modified">{formatDateTime(webPageData.infoLastModified, true)}</Info>
            </Col>
          </Row>

          {webPageData.infoLastRejectionReason !== undefined && (
            <Row>
              <Col2x>
                <s.InfoWarning>
                  <s.InfoWarningLabel>
                    <strong>Last Rejection Reason</strong>
                  </s.InfoWarningLabel>
                  <s.InfoWarningContent>{webPageData.infoLastRejectionReason}</s.InfoWarningContent>
                </s.InfoWarning>
                <br />
              </Col2x>
            </Row>
          )}

          {webPageData.infoRejectionReason !== undefined && (
            <Form config={rejectionNotesConfig}>
              <FormRow>
                <Col2x>
                  <RejectionNotesField
                    name="rejectionReason"
                    label="Reason(s) for Rejection (visible to customer)"
                    input={TextareaInput}
                    inputProps={{}}
                  />
                </Col2x>
              </FormRow>
              {webPageData.infoEnableEditRejectionReason && (
                <FormRow>
                  <Col2x>
                    <ButtonGroup margin="small">
                      <Button onClick={() => saveRejectionNotes()} loading={isRejectionNotesSubmitting} face="primary">
                        Save Notes
                      </Button>
                    </ButtonGroup>
                  </Col2x>
                </FormRow>
              )}
            </Form>
          )}

          {webPageData.infoInternalNotes !== undefined && (
            <Form config={config}>
              <FormRow>
                <Col2x>
                  <Field
                    name="notes"
                    label={webPageData.infoInternalNotesLabel}
                    input={TextareaInput}
                    inputProps={{}}
                  />
                </Col2x>
              </FormRow>
              <FormRow>
                <Col2x>
                  <ButtonGroup margin="small">
                    <Button onClick={() => saveInternalNotes()} loading={isInternalNotesSubmitting} face="primary">
                      Save Notes
                    </Button>
                  </ButtonGroup>
                </Col2x>
              </FormRow>
            </Form>
          )}
        </InfoSet>
      </PageSection>
      {webPageData.uploadedFiles !== undefined && <UploadedFilesTable webPage={webPageData} invalidate={invalidate} />}
      {webPageData.dailyReports !== undefined && <MetrcSourceTable webPage={webPageData} />}
      {webPageData.dailyReportsPOS !== undefined && <POSSourceTable webPage={webPageData} />}
      {isShowRejectDialog && (
        <RejectDialog webPage={webPageData} hideRejectDialog={hideRejectDialog} invalidate={invalidate} />
      )}
      {webPageData.history !== undefined && (
        <PageSection title="History" anchor="history">
          <Layer rounded shadowed stretch>
            <HistoryTable
              dataQa={TableDataQa.BP_SALESDATA_SOURCE_DETAILS_HISTORY}
              loading={isFetching}
              items={webPageData?.history}
              pagination={historyPagination}
            />
          </Layer>
        </PageSection>
      )}
    </Page>
  )
}

export default SourceDetails
